import {
  BookingProductType,
  BookingState,
  BookingTariff,
  SetBookingContingentReservation,
  SetProductBookingContingentReservation,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import { SetContingentReservationDate } from '@products/models/contingent.model';
import { findBookedPackageTariff, findBookedTariff } from '@store/products/booking/product/product-booking.reducer';
import { cloneDeep } from 'lodash';

/**
 * Set validated contingent reservation in booking
 * @description Set validated contingent reservation in booking
 * @param state
 * @param props
 * @returns State
 */
export const setValidatedContingentReservationInBooking = (
  state: BookingState,
  props: { setProductBookingContingentReservations: SetProductBookingContingentReservation[] }
) => {
  const setProductBookingContingentReservations: SetProductBookingContingentReservation[] = cloneDeep(
    props.setProductBookingContingentReservations
  );

  if (setProductBookingContingentReservations.length) {
    const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

    const setValidatedContingentReservation = (
      bookedTariff: BookingTariff,
      contingentReservation: SetBookingContingentReservation
    ) => {
      const { bookingReservationIndex, toDate, isValid } = contingentReservation;
      const bookedTariffContingentReservation = bookedTariff.contingentReservations[bookingReservationIndex];
      if (bookedTariffContingentReservation) {
        bookedTariffContingentReservation.toDate = toDate;
        bookedTariffContingentReservation.isValid = isValid;
      }
    };

    setProductBookingContingentReservations.forEach((productBookingContingentReservation) => {
      const {
        productType,
        ticketTypeId,
        ticketPersonId,
        voucherCode,
        packageNumber,
        packageIndex,
        contingentReservations
      } = productBookingContingentReservation;

      cloneStateProductsList.forEach((bookedProductType) => {
        if (bookedProductType.productType === productType) {
          if (isBookingProductTypeTariff(bookedProductType)) {
            const bookedTariff = findBookedTariff(bookedProductType, ticketTypeId, ticketPersonId, voucherCode);

            if (bookedTariff) {
              contingentReservations.forEach((contingentReservation) =>
                setValidatedContingentReservation(bookedTariff, contingentReservation)
              );
            }
          } else if (isBookingProductTypePackage(bookedProductType)) {
            if (bookedProductType.packageNumber === packageNumber && bookedProductType.packageIndex === packageIndex) {
              bookedProductType.productTypes.forEach((bookedPackageProductType) => {
                if (isBookingProductTypeTariff(bookedPackageProductType)) {
                  const bookedPackageTariff = findBookedPackageTariff(
                    bookedPackageProductType,
                    ticketTypeId,
                    ticketPersonId,
                    voucherCode,
                    packageNumber,
                    packageIndex
                  );

                  if (bookedPackageTariff) {
                    contingentReservations.forEach((contingentReservation) =>
                      setValidatedContingentReservation(bookedPackageTariff, contingentReservation)
                    );
                  }
                }
              });
            }
          }
        }
      });
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};

/**
 * Set contingent reservation date in booking
 * @description Set contingent reservation date in booking
 * @param state
 * @param props
 * @returns State
 */
export const setContingentReservationDateInBooking = (
  state: BookingState,
  props: { setContingentReservationDates: SetContingentReservationDate[] }
) => {
  const setContingentReservationDates: SetContingentReservationDate[] = cloneDeep(props.setContingentReservationDates);

  if (setContingentReservationDates.length) {
    const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
    const setContingentReservation = (bookedTariff: BookingTariff, index: number, duration: number, fromDate: Date) => {
      const bookedTariffContingentReservation = bookedTariff.contingentReservations[index];
      if (bookedTariffContingentReservation) {
        bookedTariffContingentReservation.duration = duration;
        bookedTariffContingentReservation.fromDate = fromDate;
        bookedTariffContingentReservation.isValid = null;
      }
    };

    setContingentReservationDates.forEach((contingentReservationDate) => {
      const {
        productType,
        ticketTypeId,
        ticketPersonId,
        voucherCode,
        packageNumber,
        packageIndex,
        duration,
        bookingReservationIndex,
        day
      } = contingentReservationDate;

      cloneStateProductsList.forEach((bookedProductType) => {
        if (bookedProductType.productType === productType) {
          if (isBookingProductTypeTariff(bookedProductType)) {
            const bookedTariff = findBookedTariff(bookedProductType, ticketTypeId, ticketPersonId, voucherCode);

            if (bookedTariff) {
              setContingentReservation(bookedTariff, bookingReservationIndex, duration, day);
            }
          } else if (isBookingProductTypePackage(bookedProductType)) {
            if (bookedProductType.packageNumber === packageNumber && bookedProductType.packageIndex === packageIndex) {
              bookedProductType.productTypes.forEach((bookedPackageProductType) => {
                if (isBookingProductTypeTariff(bookedPackageProductType)) {
                  const bookedPackageTariff = findBookedPackageTariff(
                    bookedPackageProductType,
                    ticketTypeId,
                    ticketPersonId,
                    voucherCode,
                    packageNumber,
                    packageIndex
                  );

                  if (bookedPackageTariff) {
                    setContingentReservation(bookedPackageTariff, bookingReservationIndex, duration, day);
                  }
                }
              });
            }
          }
        }
      });
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};
